import React, { useEffect, useMemo, useRef, useState } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
} from "@mui/x-data-grid";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import dayjs from "dayjs";

const theme = createTheme({
  components: {
    MuiDataGrid: {
      styleOverrides: {
        root: {
          "& .MuiDataGrid-row": { minHeight: "auto" },
        },
      },
      defaultProps: { density: "compact" },
    },
  },
});

// Method/status → color mapping
const getMethodColor = (method, statusCode) => {
  if (statusCode) {
    const code = parseInt(statusCode);
    if (code >= 100 && code < 200) return "default";
    if (code >= 200 && code < 300) return "success";
    if (code >= 300 && code < 400) return "warning";
    if (code >= 400) return "error";
  }
  const methodColors = {
    INVITE: "primary",
    BYE: "warning",
    CANCEL: "warning",
    ACK: "default",
    REGISTER: "info",
    OPTIONS: "default",
  };
  return methodColors[method] || "default";
};

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarDensitySelector />
      <GridToolbarFilterButton />
      <GridToolbarExport />
    </GridToolbarContainer>
  );
}

const SipTraceMessageGrid = ({ messages, onViewMessage, onViewLadder, capturing, isDark = false }) => {
  const errorRowBg = isDark ? "rgba(244, 67, 54, 0.15)" : "rgba(244, 67, 54, 0.06)";
  const [methodFilter, setMethodFilter] = useState("");
  const [phoneFilter, setPhoneFilter] = useState("");
  const [directionFilter, setDirectionFilter] = useState("");
  const dataGridRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive during capture
  useEffect(() => {
    if (capturing && dataGridRef.current) {
      const gridEl = dataGridRef.current.querySelector(".MuiDataGrid-virtualScroller");
      if (gridEl) {
        gridEl.scrollTop = gridEl.scrollHeight;
      }
    }
  }, [messages.length, capturing]);

  // Apply local filters
  const filteredMessages = useMemo(() => {
    let filtered = messages;

    if (methodFilter) {
      filtered = filtered.filter(
        (m) => (m.method || "").toUpperCase() === methodFilter.toUpperCase()
      );
    }
    if (phoneFilter) {
      const phone = phoneFilter.toLowerCase();
      filtered = filtered.filter(
        (m) =>
          (m.from_user || "").toLowerCase().includes(phone) ||
          (m.to_user || "").toLowerCase().includes(phone)
      );
    }
    if (directionFilter) {
      filtered = filtered.filter((m) => m.direction === directionFilter);
    }

    return filtered;
  }, [messages, methodFilter, phoneFilter, directionFilter]);

  // Extract unique methods for dropdown
  const uniqueMethods = useMemo(() => {
    const methods = new Set();
    messages.forEach((m) => {
      if (m.method) methods.add(m.method.toUpperCase());
    });
    return Array.from(methods).sort();
  }, [messages]);

  const columns = [
    {
      field: "timestamp",
      headerName: "Time",
      width: 120,
      renderCell: (params) => {
        const ts = params.row.timestamp;
        if (!ts) return "";
        return dayjs(ts).format("HH:mm:ss.SSS");
      },
    },
    {
      field: "direction",
      headerName: "Dir",
      width: 70,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const dir = params.value;
        return dir === "received" ? (
          <Tooltip title="Received">
            <CallReceivedIcon fontSize="small" sx={{ color: "#4caf50" }} />
          </Tooltip>
        ) : (
          <Tooltip title="Sent">
            <CallMadeIcon fontSize="small" sx={{ color: "#2196f3" }} />
          </Tooltip>
        );
      },
    },
    {
      field: "method",
      headerName: "Method",
      width: 110,
      renderCell: (params) => {
        const method = params.row.method || "";
        const statusCode = params.row.status_code || "";
        const label = statusCode ? `${statusCode} ${params.row.status_text || ""}`.trim() : method;
        return (
          <Chip
            label={label}
            size="small"
            color={getMethodColor(method, statusCode)}
            variant={statusCode ? "filled" : "outlined"}
            sx={{ fontSize: "0.7rem", height: 22 }}
          />
        );
      },
    },
    {
      field: "from_user",
      headerName: "From",
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" noWrap title={params.value}>
          {params.value || "-"}
        </Typography>
      ),
    },
    {
      field: "to_user",
      headerName: "To",
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" noWrap title={params.value}>
          {params.value || "-"}
        </Typography>
      ),
    },
    {
      field: "call_id",
      headerName: "Call-ID",
      width: 180,
      renderCell: (params) => {
        const cid = params.value || "";
        const short = cid.length > 20 ? cid.substring(0, 20) + "..." : cid;
        return (
          <Tooltip title={`View ladder for: ${cid}`}>
            <Typography
              variant="body2"
              sx={{
                cursor: "pointer",
                color: "primary.main",
                textDecoration: "underline",
                "&:hover": { color: "primary.dark" },
              }}
              noWrap
              onClick={(e) => {
                e.stopPropagation();
                if (cid) onViewLadder(cid);
              }}
            >
              {short || "-"}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "remote_ip",
      headerName: "Remote IP",
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.row.remote_ip}:{params.row.remote_port}
        </Typography>
      ),
    },
    {
      field: "carrier",
      headerName: "Carrier",
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value || "-"}
        </Typography>
      ),
    },
    {
      field: "bytes",
      headerName: "Size",
      width: 70,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Typography variant="body2">{params.value || 0}B</Typography>
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0}>
          <Tooltip title="View raw SIP message">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onViewMessage(params.row.id);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.call_id && (
            <Tooltip title="View ladder diagram">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onViewLadder(params.row.call_id);
                }}
              >
                <AccountTreeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  const rows = filteredMessages.map((msg, index) => ({
    ...msg,
    id: msg.id || index,
  }));

  return (
    <Box>
      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 1 }} alignItems="center" flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Method</InputLabel>
          <Select
            value={methodFilter}
            label="Method"
            onChange={(e) => setMethodFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {uniqueMethods.map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Phone Number"
          size="small"
          value={phoneFilter}
          onChange={(e) => setPhoneFilter(e.target.value)}
          sx={{ width: 160 }}
          placeholder="Search from/to..."
        />

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Direction</InputLabel>
          <Select
            value={directionFilter}
            label="Direction"
            onChange={(e) => setDirectionFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="received">Received</MenuItem>
            <MenuItem value="transmitting">Sent</MenuItem>
          </Select>
        </FormControl>

        <Typography variant="body2" color="text.secondary">
          Showing {rows.length} of {messages.length} messages
        </Typography>
      </Stack>

      {/* Grid */}
      <ThemeProvider theme={theme}>
        <Box ref={dataGridRef} sx={{ width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            autoHeight
            pageSizeOptions={[25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 50 } },
              sorting: { sortModel: [{ field: "timestamp", sort: "asc" }] },
            }}
            slots={{ toolbar: CustomToolbar }}
            disableRowSelectionOnClick
            getRowClassName={(params) => {
              const code = parseInt(params.row.status_code || 0);
              if (code >= 400) return "sip-error-row";
              return "";
            }}
            sx={{
              "& .sip-error-row": {
                bgcolor: errorRowBg,
              },
              "& .MuiDataGrid-row:hover": {
                cursor: "pointer",
              },
            }}
            onRowClick={(params) => onViewMessage(params.row.id)}
          />
        </Box>
      </ThemeProvider>
    </Box>
  );
};

export default SipTraceMessageGrid;
